// Wraps a screen that needs an account (friends, groups, a tasting session). Signed out, it says
// why and offers sign-in with a redirect back here, instead of the screen's raw "sign in required"
// error. A failed session check (offline) lets the screen through, so cached data still paints.
import { useT } from "../lib/i18n";
import { navigate } from "../lib/router";
import { useSession } from "../lib/social/auth";
import { Button, EmptyState, ScreenHeader, Screen, Skeleton } from "../ui";

export interface SignInGateProps {
  /** The route to come back to after signing in, e.g. "/friends". */
  redirect: string;
  children: React.ReactNode;
}

export function SignInGate({ redirect, children }: SignInGateProps): React.JSX.Element {
  const { t } = useT();
  const auth = useSession();
  if (auth.status === "signedIn" || auth.status === "error") return <>{children}</>;
  if (auth.status === "loading") {
    return (
      <Screen aria-busy={true}>
        <ScreenHeader backFallback="/samen" />
        <Skeleton kind="card" />
      </Screen>
    );
  }
  return (
    <Screen>
      <ScreenHeader backFallback="/samen" />
      <EmptyState
        icon="person"
        title={t("social.signin.gate.title")}
        body={t("social.signin.gate.body")}
        action={
          <Button
            tone="primary"
            onPress={() => {
              navigate(`/signin?redirect=${encodeURIComponent(redirect)}`);
            }}
          >
            {t("social.signin.gate.action")}
          </Button>
        }
      />
    </Screen>
  );
}
